import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Layout/Footer';
import { apiService } from '../services/api';
import { formatDateShort } from '../utils/dateFormat';
import AssignModal from '../components/AssignModal';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    useDroppable,
    useDraggable,
} from '@dnd-kit/core';
import {
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Draggable Task Card Component
const DraggableTaskCard: React.FC<{
    task: any;
    isDragging?: boolean;
    isUpdating?: boolean;
    darkMode?: boolean;
    canDrag?: boolean;
}> = ({ task, isDragging, isUpdating, darkMode = false, canDrag = true }) => {
    const agreementType = task.agreement_type;
    const company = task.company;
    const creator = task.creator;

    const getAgreementTypeIcon = (agreementType: any) => {
        if (!agreementType) return 'fa-file';
        const name = agreementType.name?.toUpperCase() || '';
        if (name.includes('PKS') || name.includes('PARTNERSHIP')) return 'fa-handshake';
        if (name.includes('PO') || name.includes('PURCHASE')) return 'fa-file-invoice';
        if (name.includes('BAST')) return 'fa-file-alt';
        if (name.includes('MOU')) return 'fa-file-signature';
        if (name.includes('INVOICE')) return 'fa-file-invoice';
        return 'fa-file';
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const iconClass = getAgreementTypeIcon(agreementType);

    const isReleased = task.status?.name === 'RELEASED' || task.status_id === 6;
    const canDragEffective = canDrag && !isReleased;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging: isDraggingState,
    } = useDraggable({
        id: task.id.toString(),
        disabled: !canDragEffective,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDraggingState ? 0.5 : (isReleased ? 0.8 : 1),
        ...(!canDragEffective ? { cursor: 'default' } : {})
    };

    const navigate = useNavigate();

    const handleClick = (e: React.MouseEvent) => {
        // Only navigate if not dragging
        if (!isDraggingState) {
            navigate(`/agreement/${task.id}`);
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...listeners}
            {...attributes}
            onClick={handleClick}
            className={`border rounded-lg p-4 shadow-sm hover:shadow-md transition-colors duration-300 ${canDragEffective ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
                } ${isReleased
                    ? (darkMode ? 'bg-gray-800/30 border-gray-800 grayscale' : 'bg-gray-100 border-gray-200 grayscale text-gray-500')
                    : (darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')
                } ${isUpdating ? 'opacity-50' : ''}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <div className={`w-6 h-6 rounded flex items-center justify-center ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'
                        }`}>
                        <i className={`fa-solid ${iconClass} text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'
                            }`}></i>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                        }`}>
                        {agreementType?.name || 'N/A'}
                    </span>
                    {task.template_type && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${task.template_type === 'Internal'
                            ? darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                            : darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                            }`}>
                            {task.template_type}
                        </span>
                    )}
                </div>
            </div>
            <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.title}</h4>
            {company && (
                <p className={`text-xs mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{company.name}</p>
            )}
            <div className={`flex items-center justify-between text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className="flex items-center space-x-1">
                    {creator?.avatar_url ? (
                        <img src={creator.avatar_url} className="w-5 h-5 rounded-full" alt={creator.name} />
                    ) : (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'
                            }`}>
                            <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{creator?.name?.charAt(0) || 'U'}</span>
                        </div>
                    )}
                    <span>{creator?.name || 'Unknown'}</span>
                </div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                    <i className="fa-solid fa-calendar mr-1"></i>
                    Created {formatDateShort(task.created_at)}
                </span>
            </div>
            {isUpdating && (
                <div className="mt-2 flex items-center justify-center">
                    <i className="fa-solid fa-spinner fa-spin text-violet-600 text-xs"></i>
                </div>
            )}
        </div>
    );
};

// Droppable Column Component
const DroppableColumn: React.FC<{
    status: any;
    tasks: any[];
    colors: any;
    getTasksByStatus: (statusId: number) => any[];
    updatingTask: number | null;
    darkMode?: boolean;
    canDrag?: boolean;
}> = ({ status, tasks, colors, getTasksByStatus, updatingTask, darkMode = false, canDrag = true }) => {
    const statusTasks = getTasksByStatus(status.id);
    const { setNodeRef, isOver } = useDroppable({
        id: status.id.toString(),
    });

    return (
        <div className="w-80 flex flex-col">
            <div className={`${colors.bg} border ${colors.border} rounded-t-lg px-4 py-3`}>
                <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-semibold ${colors.text}`}>{status.name}</h3>
                    <span className={`px-2 py-1 ${colors.badge} text-white text-xs font-bold rounded`}>
                        {statusTasks.length}
                    </span>
                </div>
            </div>
            <div
                ref={setNodeRef}
                className={`border-l border-r border-b ${colors.border} rounded-b-lg flex-1 p-4 space-y-4 overflow-y-auto transition-colors ${darkMode ? 'bg-gray-800' : 'bg-white'
                    } ${isOver ? (darkMode ? 'bg-violet-900/30' : 'bg-violet-50') : ''
                    }`}
            >
                {statusTasks.length === 0 ? (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <i className="fa-solid fa-inbox text-2xl mb-2"></i>
                        <p className="text-sm">Tidak ada task</p>
                    </div>
                ) : (
                    statusTasks.map((task) => (
                        <DraggableTaskCard
                            key={task.id}
                            task={task}
                            isUpdating={updatingTask === task.id}
                            darkMode={darkMode}
                            canDrag={canDrag}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

const AgreementBoardV2: React.FC = () => {
    const { isCollapsed, toggleSidebar } = useSidebar();
    const { user } = useAuth();
    const { darkMode } = useTheme();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
    const [showModal, setShowModal] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingAgreementTypes, setLoadingAgreementTypes] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [formData, setFormData] = useState({
        categoryId: '',
        agreementTypeId: '',
        title: '',
        description: '',
        pid: '',
        templateType: 'Internal',
        templateId: ''
    });
    const [error, setError] = useState<string>('');
    const [uploadingTemplate, setUploadingTemplate] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Board data
    const [statuses, setStatuses] = useState<any[]>([]);
    const [tasks, setTasks] = useState<any[]>([]);
    const [loadingStatuses, setLoadingStatuses] = useState(false);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [activeTask, setActiveTask] = useState<any | null>(null);
    const [updatingTask, setUpdatingTask] = useState<number | null>(null);
    const [viewType, setViewType] = useState<'kanban' | 'list' | 'repository'>('kanban');
    const [filterType, setFilterType] = useState<'all' | 'issued_by_me' | 'assigned_to_me'>('all');
    const [templateTypeFilter, setTemplateTypeFilter] = useState<'all' | 'Internal' | 'External'>('all');

    // Assign Modal State
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignTargetTask, setAssignTargetTask] = useState<{ id: number; statusId: number } | null>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);

    // Load statuses dan tasks saat component mount atau filter/search berubah
    // Load statuses dan tasks saat component mount atau filter/search berubah, dan auto-refresh setiap 15 detik
    useEffect(() => {
        const loadBoardData = async (isBackground = false) => {
            if (!isBackground) {
                setLoadingStatuses(true);
                setLoadingTasks(true);
            }
            try {
                const tasksParams: any = { is_active: 1 };
                if (filterType !== 'all') {
                    tasksParams.filter_type = filterType;
                }
                if (searchQuery.trim()) {
                    tasksParams.search = searchQuery.trim();
                }
                if (templateTypeFilter !== 'all') {
                    tasksParams.template_type = templateTypeFilter;
                }

                const [statusesData, tasksData, usersData] = await Promise.all([
                    apiService.getStatuses(),
                    apiService.getTasks(tasksParams),
                    apiService.getUsers()
                ]);
                setStatuses(statusesData);
                setTasks(tasksData);
                setUsers(usersData);
            } catch (err: any) {
                console.error('Error loading board data:', err);
                if (!isBackground) {
                    setError('Gagal memuat data board');
                }
            } finally {
                if (!isBackground) {
                    setLoadingStatuses(false);
                    setLoadingTasks(false);
                }
            }
        };

        // Initial load
        loadBoardData();

        // Set interval for auto-refresh (15 seconds)
        const intervalId = setInterval(() => {
            loadBoardData(true);
        }, 15000);

        // Cleanup interval on unmount or dependency change
        return () => clearInterval(intervalId);
    }, [filterType, searchQuery, templateTypeFilter]);

    // Load categories, agreement types, dan companies saat modal dibuka
    useEffect(() => {
        if (showModal) {
            const loadModalData = async () => {
                setLoadingCategories(true);
                setLoadingAgreementTypes(true);
                setLoadingCompanies(true);
                try {
                    const [cats, types, comps] = await Promise.all([
                        apiService.getCategories(),
                        apiService.getAgreementTypes(),
                        apiService.getCompanies()
                    ]);
                    setCategories(cats);
                    setAgreementTypes(types);
                    setCompanies(comps.filter((c: any) => c.is_active !== false));
                } catch (err: any) {
                    setError('Gagal memuat data category, agreement type, dan companies');
                    console.error('Error loading data:', err);
                } finally {
                    setLoadingCategories(false);
                    setLoadingAgreementTypes(false);
                    setLoadingCompanies(false);
                }
            };
            loadModalData();
        } else {
            // Reset templates saat modal ditutup
            setAvailableTemplates([]);
        }
    }, [showModal]);

    // Load available templates saat categoryId, agreementTypeId, atau templateType berubah
    useEffect(() => {
        const loadTemplates = async () => {
            if (!formData.categoryId || !formData.agreementTypeId) {
                setAvailableTemplates([]);
                setFormData(prev => ({ ...prev, templateId: '' }));
                return;
            }

            setLoadingTemplates(true);
            try {
                const templates = await apiService.getTemplates({
                    category_id: Number(formData.categoryId),
                    agreement_type_id: Number(formData.agreementTypeId),
                    template_type: formData.templateType,
                    is_active: 1
                });
                setAvailableTemplates(templates || []);
                // Reset templateId jika template yang dipilih tidak ada di list baru
                if (formData.templateId && !templates.find((t: any) => t.id === Number(formData.templateId))) {
                    setFormData(prev => ({ ...prev, templateId: '' }));
                }
            } catch (err: any) {
                console.error('Error loading templates:', err);
                setAvailableTemplates([]);
                setFormData(prev => ({ ...prev, templateId: '' }));
            } finally {
                setLoadingTemplates(false);
            }
        };

        // Debounce untuk menghindari terlalu banyak request
        const timeoutId = setTimeout(loadTemplates, 500);
        return () => clearTimeout(timeoutId);
    }, [formData.categoryId, formData.agreementTypeId, formData.templateType]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleModalSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.categoryId || !formData.agreementTypeId || !formData.title.trim() || !formData.templateId) {
            setError('Silakan lengkapi semua field yang wajib diisi');
            return;
        }
        const selectedTemplate = availableTemplates.find(t => t.id === Number(formData.templateId));
        if (!selectedTemplate) {
            setError('Template yang dipilih tidak valid. Silakan pilih template yang tersedia.');
            return;
        }
        setError('');
        setShowModal(false);
        // Navigate ke /new-request dengan state data
        navigate('/new-request', {
            state: {
                categoryId: Number(formData.categoryId),
                agreementTypeId: Number(formData.agreementTypeId),
                templateType: formData.templateType,
                title: formData.title,
                description: formData.description,
                pid: formData.pid,
                category: categories.find(c => c.id === Number(formData.categoryId)),
                agreementType: agreementTypes.find(t => t.id === Number(formData.agreementTypeId)),
                templateId: selectedTemplate.id,
                template: selectedTemplate
            }
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            categoryId: '',
            agreementTypeId: '',
            title: '',
            description: '',
            pid: '',
            templateType: 'Internal',
            templateId: ''
        });
        setError('');
        setAvailableTemplates([]);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validasi file type
        const isDocx = file.name.toLowerCase().endsWith('.docx') ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        if (!isDocx) {
            setError('File harus berformat .docx');
            return;
        }

        if (!formData.categoryId || !formData.agreementTypeId || !formData.title.trim()) {
            setError('Mohon lengkapi Nama PT, Category, dan Agreement Type terlebih dahulu');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setUploadingTemplate(true);
        setError('');

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('file', file);

            // Gunakan Nama PT + Agreement Type sebagai title template jika user tidak input spesifik (tapi di sini kita pakai nama PT dari form)
            // Atau format: [External] Nama PT - Agreement Type
            const agreementTypeName = agreementTypes.find(t => t.id === Number(formData.agreementTypeId))?.name || '';
            const templateTitle = `[External] ${formData.title} - ${agreementTypeName}`;

            formDataToSend.append('title', templateTitle);
            formDataToSend.append('description', `External agreement for ${formData.title}`);
            formDataToSend.append('category_id', formData.categoryId);
            formDataToSend.append('agreement_type_id', formData.agreementTypeId);
            formDataToSend.append('template_type', 'External');

            // External templates do not have variables extracted
            // Send empty variables list
            formDataToSend.append('variables', JSON.stringify([]));

            const newTemplate = await apiService.createTemplate(formDataToSend);

            // Add new template to available templates
            setAvailableTemplates(prev => [...prev, newTemplate]);

            // Select the new template
            setFormData(prev => ({
                ...prev,
                templateId: String(newTemplate.id)
            }));

            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

        } catch (err: any) {
            console.error('Error uploading template:', err);
            setError(err.message || 'Gagal mengupload dokumen external');
            if (fileInputRef.current) fileInputRef.current.value = '';
        } finally {
            setUploadingTemplate(false);
        }
    };

    // Helper function untuk extract color classes dari status
    const getStatusColors = (colorClass: string | null) => {
        if (!colorClass) {
            return {
                bg: 'bg-gray-100',
                border: 'border-gray-200',
                text: 'text-gray-900',
                badge: 'bg-gray-500'
            };
        }

        // Extract background color (e.g., "bg-purple-100" -> "purple")
        const bgMatch = colorClass.match(/bg-(\w+)-(\d+)/);
        if (bgMatch) {
            const color = bgMatch[1];
            const shade = bgMatch[2];
            return {
                bg: `bg-${color}-100`,
                border: `border-${color}-200`,
                text: `text-${color}-900`,
                badge: `bg-${color}-500`
            };
        }

        return {
            bg: 'bg-gray-100',
            border: 'border-gray-200',
            text: 'text-gray-900',
            badge: 'bg-gray-500'
        };
    };

    // Helper function untuk format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Helper function untuk get agreement type icon
    const getAgreementTypeIcon = (agreementType: any) => {
        if (!agreementType) return 'fa-file';
        const name = agreementType.name?.toUpperCase() || '';
        if (name.includes('PKS') || name.includes('PARTNERSHIP')) return 'fa-handshake';
        if (name.includes('PO') || name.includes('PURCHASE')) return 'fa-file-invoice';
        if (name.includes('BAST')) return 'fa-file-alt';
        if (name.includes('MOU')) return 'fa-file-signature';
        if (name.includes('INVOICE')) return 'fa-file-invoice';
        return 'fa-file';
    };

    // Get tasks by status_id
    const getTasksByStatus = (statusId: number | null) => {
        if (!statusId) return [];
        return tasks
            .filter(task => task.status_id === statusId)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    };

    const canManageBoard = user?.scopes?.includes('task.board') ?? false;

    // Drag and drop handlers
    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const task = tasks.find(t => t.id === Number(active.id));
        setActiveTask(task || null);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveTask(null);

        if (!over) return;

        const taskId = Number(active.id);
        const newStatusId = Number(over.id);

        // Find task
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;

        // If dropped in the same status, do nothing
        if (task.status_id === newStatusId) return;

        // Check if moving from DRAFTING to REVIEW (or BUSINESS REVIEW)
        // Adjust status names/IDs as per your system configuration
        const newStatus = statuses.find(s => s.id === newStatusId);
        const oldStatus = statuses.find(s => s.id === task.status_id);

        // If explicitly moving to Review phase, show assign modal
        // Assuming status_id 3 is Review/Business Review (based on TaskDetail.tsx)
        // Or check name for robustness
        const isMovingToReview = newStatusId === 3 || newStatus?.name?.toUpperCase().includes('REVIEW');
        const isFromDrafting = task.status_id === 2 || oldStatus?.name?.toUpperCase().includes('DRAFTING');

        if (isMovingToReview && isFromDrafting) {
            setAssignTargetTask({ id: taskId, statusId: newStatusId });
            setShowAssignModal(true);
            return; // Stop here, wait for modal
        }

        // Optimistic update
        setUpdatingTask(taskId);
        const updatedTasks = tasks.map(t =>
            t.id === taskId ? { ...t, status_id: newStatusId } : t
        );
        setTasks(updatedTasks);

        try {
            // Update task status via API
            await apiService.updateTask(taskId, {
                status_id: newStatusId
            });
        } catch (err: any) {
            console.error('Error updating task status:', err);
            // Revert optimistic update on error
            setTasks(tasks);
            setError('Gagal mengupdate status task');
        } finally {
            setUpdatingTask(null);
        }
    };

    const handleAssignTask = async (userId: number, comment?: string) => {
        if (!assignTargetTask) return;

        setIsAssigning(true);
        try {
            // 1. Create Assignment
            await apiService.createTaskAssignment({
                task_id: assignTargetTask.id,
                user_id: userId,
                comment: comment
            });

            // 2. Update Status
            await apiService.updateTask(assignTargetTask.id, {
                status_id: assignTargetTask.statusId
            });

            // 3. Update Local State
            const updatedTasks = tasks.map(t =>
                t.id === assignTargetTask.id ? { ...t, status_id: assignTargetTask.statusId } : t
            );
            setTasks(updatedTasks);

            // 4. Close Modal
            setShowAssignModal(false);
            setAssignTargetTask(null);
        } catch (err: any) {
            console.error('Error assigning and updating task:', err);
            setError(err.message || 'Gagal melakukan assignment dan update status');
        } finally {
            setIsAssigning(false);
        }
    };

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    return (
        <>
            <AssignModal
                isOpen={showAssignModal}
                onClose={() => setShowAssignModal(false)}
                onAssign={handleAssignTask}
                users={users}
                title="Assign to Reviewer"
                isSubmitting={isAssigning}
            />
            <div className="flex" style={{ height: 'calc(100vh - 56px)' }}>
                <aside
                    className={`border-r fixed left-0 top-14 bottom-0 flex flex-col transition-all duration-300 z-40 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        } ${isCollapsed ? 'w-16' : 'w-64'}`}
                >
                    <div className="flex-1 overflow-y-auto">
                        {isCollapsed ? (
                            <div className="pt-6 pb-2 space-y-2">
                                <div className="px-2">
                                    <button
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${searchQuery
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title={searchQuery ? `Search: ${searchQuery}` : 'Search'}
                                        onClick={() => {
                                            // Toggle sidebar jika collapsed dan ada search query
                                            if (searchQuery && isCollapsed) {
                                                toggleSidebar();
                                            }
                                        }}
                                    >
                                        <i className="fa-solid fa-magnifying-glass text-sm"></i>
                                    </button>
                                </div>
                                <div className={`px-2 py-2 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <button
                                        onClick={() => setViewType('kanban')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${viewType === 'kanban'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="Kanban Board"
                                    >
                                        <i className="fa-solid fa-columns text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => setViewType('list')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${viewType === 'list'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="List View"
                                    >
                                        <i className="fa-solid fa-list text-sm"></i>
                                    </button>
                                </div>
                                <div className={`px-2 py-4 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <button
                                        onClick={() => setFilterType('all')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${filterType === 'all'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="All Tasks"
                                    >
                                        <i className="fa-solid fa-list text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => setFilterType('issued_by_me')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${filterType === 'issued_by_me'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="Issued by me"
                                    >
                                        <i className="fa-solid fa-user text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => setFilterType('assigned_to_me')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${filterType === 'assigned_to_me'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="Assigned to me"
                                    >
                                        <i className="fa-solid fa-user-check text-sm"></i>
                                    </button>
                                </div>
                                <div className={`px-2 py-4 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <button
                                        onClick={() => setTemplateTypeFilter('all')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${templateTypeFilter === 'all'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="All Types"
                                    >
                                        <i className="fa-solid fa-list text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => setTemplateTypeFilter('Internal')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${templateTypeFilter === 'Internal'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="Internal"
                                    >
                                        <i className="fa-solid fa-building text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => setTemplateTypeFilter('External')}
                                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${templateTypeFilter === 'External'
                                            ? 'text-violet-600 bg-violet-50'
                                            : darkMode
                                                ? 'text-gray-300 hover:bg-gray-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        title="External"
                                    >
                                        <i className="fa-solid fa-globe text-sm"></i>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="px-4 pt-6 pb-4">
                                    <div className="relative">
                                        <i className={`fa-solid fa-magnifying-glass absolute left-3 top-3 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'
                                            }`}></i>
                                        <input
                                            type="text"
                                            placeholder="Search agreements..."
                                            value={searchQuery}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setSearchQuery(value);

                                                // Update URL search params
                                                const params = new URLSearchParams(searchParams);
                                                if (value.trim()) {
                                                    params.set('search', value.trim());
                                                } else {
                                                    params.delete('search');
                                                }
                                                setSearchParams(params);
                                            }}
                                            className={`w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:border-violet-500 transition-colors ${darkMode
                                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                                : 'border-gray-300'
                                                }`}
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    const params = new URLSearchParams(searchParams);
                                                    params.delete('search');
                                                    setSearchParams(params);
                                                }}
                                                className={`absolute right-3 top-3 transition-colors ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                            >
                                                <i className="fa-solid fa-times text-sm"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={`px-4 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                            }`}>Board Views</span>
                                    </div>
                                    <ul className="space-y-1">
                                        <li>
                                            <button
                                                onClick={() => setViewType('kanban')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${viewType === 'kanban'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-columns mr-3 text-sm ${viewType === 'kanban' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={viewType === 'kanban' ? 'font-medium' : ''}>Kanban Board</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => setViewType('list')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${viewType === 'list'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-list mr-3 text-sm ${viewType === 'list' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={viewType === 'list' ? 'font-medium' : ''}>List View</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                <div className={`px-4 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                            }`}>Repository</span>
                                    </div>
                                    <ul className="space-y-1">
                                        <li>
                                            <button
                                                onClick={() => setViewType('repository')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${viewType === 'repository'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-folder-open mr-3 text-sm ${viewType === 'repository' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={viewType === 'repository' ? 'font-medium' : ''}>Released Documents</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                <div className={`px-4 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                        }`}>Filters</span>
                                    <ul className="mt-2 space-y-1">
                                        <li>
                                            <button
                                                onClick={() => setFilterType('all')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'all'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-list mr-3 text-sm ${filterType === 'all' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={filterType === 'all' ? 'font-medium' : ''}>All Tasks</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => setFilterType('issued_by_me')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'issued_by_me'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-user mr-3 text-sm ${filterType === 'issued_by_me' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={filterType === 'issued_by_me' ? 'font-medium' : ''}>Issued by me</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => setFilterType('assigned_to_me')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'assigned_to_me'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-user-check mr-3 text-sm ${filterType === 'assigned_to_me' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={filterType === 'assigned_to_me' ? 'font-medium' : ''}>Assigned to me</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                <div className={`px-4 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                        }`}>Template Type</span>
                                    <ul className="mt-2 space-y-1">
                                        <li>
                                            <button
                                                onClick={() => setTemplateTypeFilter('all')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${templateTypeFilter === 'all'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-list mr-3 text-sm ${templateTypeFilter === 'all' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={templateTypeFilter === 'all' ? 'font-medium' : ''}>All Types</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => setTemplateTypeFilter('Internal')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${templateTypeFilter === 'Internal'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-building mr-3 text-sm ${templateTypeFilter === 'Internal' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={templateTypeFilter === 'Internal' ? 'font-medium' : ''}>Internal</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => setTemplateTypeFilter('External')}
                                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${templateTypeFilter === 'External'
                                                    ? 'bg-violet-50 text-violet-600'
                                                    : darkMode
                                                        ? 'text-gray-300 hover:bg-gray-700'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <i className={`fa-solid fa-globe mr-3 text-sm ${templateTypeFilter === 'External' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                                <span className={templateTypeFilter === 'External' ? 'font-medium' : ''}>External</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={`p-2 border-t mt-auto ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                            onClick={toggleSidebar}
                            className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${darkMode
                                ? 'text-gray-300 hover:bg-gray-700'
                                : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            <i className={`fa-solid ${isCollapsed ? 'fa-angles-right' : 'fa-angles-left'} text-sm`}></i>
                        </button>
                    </div>
                </aside>

                <main
                    className={`flex-1 overflow-hidden transition-all duration-300 relative ${darkMode ? 'bg-gray-900' : 'bg-gray-50'
                        }`}
                    style={{
                        marginLeft: isCollapsed ? '64px' : '256px',
                        paddingBottom: '64px'
                    }}
                >
                    <section
                        className={`border-b px-6 py-4 fixed z-30 transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                        style={{
                            left: isCollapsed ? '64px' : '256px',
                            right: 0,
                            top: '56px'
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Agreements Board V2</h1>
                                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Kanban view of all agreement workflows</p>
                            </div>
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                                >
                                    <i className="fa-solid fa-plus mr-2"></i>
                                    New Request
                                </button>
                            </div>
                        </div>
                    </section>

                    <section
                        className="overflow-x-auto overflow-y-auto absolute inset-0"
                        style={{
                            top: '100px',
                            bottom: '64px',
                            left: 0,
                            right: 0
                        }}
                    >
                        <div className="p-6">
                            {loadingStatuses || loadingTasks ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <i className="fa-solid fa-spinner fa-spin text-4xl text-violet-600 mb-4"></i>
                                        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat board...</p>
                                    </div>
                                </div>
                            ) : viewType === 'kanban' ? (
                                statuses.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <i className={`fa-solid fa-inbox text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                                            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Tidak ada status yang tersedia</p>
                                        </div>
                                    </div>
                                ) : (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragStart={handleDragStart}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <div className="flex space-x-6 min-w-max">
                                            {statuses.map((status) => {
                                                const colors = getStatusColors(status.color_class);
                                                return (
                                                    <DroppableColumn
                                                        key={status.id}
                                                        status={status}
                                                        tasks={tasks}
                                                        colors={colors}
                                                        getTasksByStatus={getTasksByStatus}
                                                        updatingTask={updatingTask}
                                                        darkMode={darkMode}
                                                        canDrag={canManageBoard}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <DragOverlay>
                                            {activeTask ? (
                                                <div className={`border rounded-lg p-4 shadow-lg w-72 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                                    }`}>
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center space-x-2">
                                                            <div className={`w-6 h-6 rounded flex items-center justify-center ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'
                                                                }`}>
                                                                <i className={`fa-solid ${getAgreementTypeIcon(activeTask.agreement_type)} text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'
                                                                    }`}></i>
                                                            </div>
                                                            <span className={`px-2 py-1 text-xs font-medium rounded ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                                                                }`}>
                                                                {activeTask.agreement_type?.name || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{activeTask.title}</h4>
                                                    {activeTask.company && (
                                                        <p className={`text-xs mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{activeTask.company.name}</p>
                                                    )}
                                                </div>
                                            ) : null}
                                        </DragOverlay>
                                    </DndContext>
                                )
                            ) : (
                                // List or Repository View
                                <div className={`rounded-lg shadow-sm border overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                    }`}>
                                    <div className="overflow-x-auto">
                                        <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                            <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
                                                <tr>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Title
                                                    </th>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Category
                                                    </th>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Agreement Type
                                                    </th>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Company
                                                    </th>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Status
                                                    </th>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Creator
                                                    </th>
                                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Created
                                                    </th>
                                                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                                                {(viewType === 'repository' 
                                                    ? tasks.filter(t => {
                                                        const name = t.status?.name?.toUpperCase() || '';
                                                        return name.includes('FINAL') || name.includes('RELEASE') || name.includes('APPROVED') || name.includes('COMPLETED');
                                                    })
                                                    : tasks
                                                ).length === 0 ? (
                                                    <tr>
                                                        <td colSpan={8} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                            <i className="fa-solid fa-inbox text-2xl mb-2 block"></i>
                                                            Tidak ada task
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    (viewType === 'repository' 
                                                        ? tasks.filter(t => {
                                                            const name = t.status?.name?.toUpperCase() || '';
                                                            return name.includes('FINAL') || name.includes('RELEASE') || name.includes('APPROVED') || name.includes('COMPLETED');
                                                        })
                                                        : tasks
                                                    ).map((task) => {
                                                        const statusColors = getStatusColors(task.status?.color_class);
                                                        const formatDate = (dateString: string) => {
                                                            const date = new Date(dateString);
                                                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                        };
                                                        return (
                                                            <tr key={task.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.title}</div>
                                                                    {task.description && (
                                                                        <div className={`text-xs mt-1 line-clamp-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{task.description}</div>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <div className="flex items-center">
                                                                        {task.category?.icon_class && (
                                                                            <i className={`${task.category.icon_class} mr-2`}></i>
                                                                        )}
                                                                        <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.category?.name || 'N/A'}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <div className="flex items-center space-x-2">
                                                                        <div className="flex items-center">
                                                                            <i className={`fa-solid ${getAgreementTypeIcon(task.agreement_type)} mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'
                                                                                }`}></i>
                                                                            <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.agreement_type?.name || 'N/A'}</span>
                                                                        </div>
                                                                        {task.template_type && (
                                                                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${task.template_type === 'Internal'
                                                                                ? darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                                                                                : darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                                                                                }`}>
                                                                                {task.template_type}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.company?.name || '-'}</span>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    {task.status && (
                                                                        <span className={`px-2 py-1 text-xs font-medium rounded ${statusColors.badge} text-white`}>
                                                                            {task.status.name}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <div className="flex items-center">
                                                                        {task.creator?.avatar_url ? (
                                                                            <img src={task.creator.avatar_url} className="w-6 h-6 rounded-full mr-2" alt={task.creator.name} />
                                                                        ) : (
                                                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'
                                                                                }`}>
                                                                                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{task.creator?.name?.charAt(0) || 'U'}</span>
                                                                            </div>
                                                                        )}
                                                                        <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.creator?.name || 'Unknown'}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        <i className="fa-solid fa-calendar mr-1"></i>
                                                                        {formatDateShort(task.created_at)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                    <button
                                                                        onClick={() => navigate(`/agreement/${task.id}`)}
                                                                        className="text-violet-600 hover:text-violet-900 mr-3"
                                                                        title="View"
                                                                    >
                                                                        <i className="fa-solid fa-eye"></i>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => navigate(`/agreement/${task.id}`)}
                                                                        className="text-blue-600 hover:text-blue-900"
                                                                        title="Edit"
                                                                    >
                                                                        <i className="fa-solid fa-edit"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </main>
            </div>
            <Footer sidebarWidth={isCollapsed ? 64 : 256} />

            {/* Modal New Request */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className={`rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'
                        }`}>
                        <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}>
                            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>New Agreement Request</h2>
                            <button
                                onClick={handleCloseModal}
                                className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                            >
                                <i className="fa-solid fa-times text-lg"></i>
                            </button>
                        </div>

                        <form onSubmit={handleModalSubmit} className="p-6 space-y-6">
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Nama PT <span className="text-red-500">*</span>
                                </label>
                                {loadingCompanies ? (
                                    <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                                        <span>Memuat daftar PT...</span>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Ketik atau pilih nama PT"
                                            list="companies-list"
                                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                                : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        <datalist id="companies-list">
                                            {companies.map((company) => (
                                                <option key={company.id} value={company.name} />
                                            ))}
                                        </datalist>
                                    </>
                                )}
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Masukkan description agreement (opsional)"
                                    rows={3}
                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${darkMode
                                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                        : 'border-gray-300'
                                        }`}
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    PID
                                </label>
                                <input
                                    type="text"
                                    name="pid"
                                    value={formData.pid}
                                    onChange={handleInputChange}
                                    placeholder="Masukkan PID (opsional)"
                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                        : 'border-gray-300'
                                        }`}
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Template Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="templateType"
                                    value={formData.templateType}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                                        ? 'bg-gray-900 border-gray-700 text-white'
                                        : 'border-gray-300'
                                        }`}
                                    required
                                >
                                    <option value="Internal">Internal</option>
                                    <option value="External">External</option>
                                </select>
                                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Pilih jenis template: Internal untuk dokumen internal, External untuk dokumen eksternal
                                </p>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Category <span className="text-red-500">*</span>
                                </label>
                                {loadingCategories ? (
                                    <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                                        <span>Memuat categories...</span>
                                    </div>
                                ) : (
                                    <select
                                        name="categoryId"
                                        value={formData.categoryId}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                                            ? 'bg-gray-900 border-gray-700 text-white'
                                            : 'border-gray-300'
                                            }`}
                                        required
                                    >
                                        <option value="">-- Pilih Category --</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Agreement Type <span className="text-red-500">*</span>
                                </label>
                                {loadingAgreementTypes ? (
                                    <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                                        <span>Memuat agreement types...</span>
                                    </div>
                                ) : (
                                    <select
                                        name="agreementTypeId"
                                        value={formData.agreementTypeId}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                                            ? 'bg-gray-900 border-gray-700 text-white'
                                            : 'border-gray-300'
                                            }`}
                                        required
                                    >
                                        <option value="">-- Pilih Agreement Type --</option>
                                        {agreementTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Template Selection */}
                            {formData.categoryId && formData.agreementTypeId && (
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Template <span className="text-red-500">*</span>
                                    </label>
                                    {loadingTemplates ? (
                                        <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                                            <span>Memuat template yang tersedia...</span>
                                        </div>
                                    ) : formData.templateType === 'External' ? (
                                        // Always show upload for External
                                        <div className={`p-4 border rounded-lg transition-colors duration-300 ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                            <div className="flex items-start space-x-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-600'}`}>
                                                    <i className="fa-solid fa-file-arrow-up"></i>
                                                </div>
                                                <div className="flex-1">
                                                    {formData.templateId ? (
                                                        <div className="mb-3">
                                                            <p className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                                                                <i className="fa-solid fa-check-circle mr-2"></i>
                                                                Document Uploaded
                                                            </p>
                                                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                                {availableTemplates.find(t => String(t.id) === String(formData.templateId))?.title || 'Selected Template'}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            Upload External Document
                                                        </p>
                                                    )}

                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileUpload}
                                                        accept=".docx"
                                                        className="hidden"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={uploadingTemplate}
                                                        className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                                                            ? 'bg-violet-600 text-white hover:bg-violet-700'
                                                            : 'bg-violet-600 text-white hover:bg-violet-700'
                                                            }`}
                                                    >
                                                        {uploadingTemplate ? (
                                                            <>
                                                                <i className="fa-solid fa-spinner fa-spin"></i>
                                                                <span>Uploading...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className={`fa-solid ${formData.templateId ? 'fa-rotate' : 'fa-upload'}`}></i>
                                                                <span>{formData.templateId ? 'Change Document' : 'Choose File (.docx)'}</span>
                                                            </>
                                                        )}
                                                    </button>
                                                    <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        Upload dokumen .docx dari komputer Anda.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : availableTemplates.length > 0 ? (
                                        <select
                                            name="templateId"
                                            value={formData.templateId}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                                                ? 'bg-gray-900 border-gray-700 text-white'
                                                : 'border-gray-300'
                                                }`}
                                            required
                                        >
                                            <option value="">-- Pilih Template --</option>
                                            {availableTemplates.map((template) => (
                                                <option key={template.id} value={template.id}>
                                                    {template.title} {template.description ? `- ${template.description}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className={`p-4 border rounded-lg transition-colors duration-300 ${darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
                                            }`}>
                                            <div className="flex items-start space-x-3">
                                                <i className={`fa-solid fa-exclamation-triangle mt-0.5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}></i>
                                                <div className="flex-1">
                                                    <p className={`text-sm font-medium ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                                                        Tidak ada template yang tersedia untuk category, agreement type, dan template type ini.
                                                    </p>
                                                    <p className={`text-xs mt-1 ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                                                        Silakan buat template terlebih dahulu di halaman Templates.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {error && (
                                <div className={`px-4 py-2 border rounded-lg text-sm transition-colors duration-300 ${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
                                    }`}>
                                    {error}
                                </div>
                            )}

                            <div className={`flex justify-end space-x-3 pt-4 border-t transition-colors duration-300 ${darkMode ? 'border-gray-700' : 'border-gray-200'
                                }`}>
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className={`px-6 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode
                                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    disabled={!formData.templateId || loadingTemplates || availableTemplates.length === 0}
                                    className="px-6 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </form>
                    </div >
                </div >
            )}
        </>
    );
};

export default AgreementBoardV2;
